import { Lock, Mail, PenLine, Send } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { DOC_TYPES, type AgreementDocType } from '@/lib/agreements/constants';

interface SendForSignatureDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    docType: AgreementDocType;
    /**
     * WHAT THE ADMIN IS ABOUT TO SEND, BY NAME — `BoardSlot.title` [D-B45].
     *
     * IT MATTERS MOST ON THE SLOT THAT HAS NO DOCUMENT YET, which is the harder case and the one
     * this dialog exists for: confirming here is what CALLS `agreement_create`, so there is no
     * title to read off a document and the doc-type label is a guess that is now wrong half the
     * time. `INSURANCE_LOAN` publishes two instruments — an Insurance Premium Advance and an
     * Insurance Premium Payment Record — and an admin who confirmed "Send the Insurance Premium
     * Advance for signature?" and received the other one was told the wrong thing by the one
     * sentence they were asked to agree to.
     *
     * OPTIONAL, WITH THE DOC-TYPE LABEL BEHIND IT, so a caller that has no slot to hand still
     * names something true. A caller that HAS one must pass `slot.title`.
     */
    title?: string | null;
    /** Present once the document exists; absent when this call is what generates it. */
    referenceNo?: string | null;
    driverName: string | null;
    witnessName: string | null;
    isPending: boolean;
    onConfirm: () => void;
}

/**
 * The send confirmation, which exists to name the channel each party is reached on.
 *
 * There is no email in this version: the driver sees the document in the Primelane driver
 * app, and the witness signs through a link the admin copies and shares by hand. Saying so
 * here is the difference between an admin who knows a link is waiting to be sent and one
 * who assumes the system sent it.
 */
export default function SendForSignatureDialog({
    open,
    onOpenChange,
    docType,
    title,
    referenceNo,
    driverName,
    witnessName,
    isPending,
    onConfirm,
}: SendForSignatureDialogProps) {
    // The seats are the ARC's and stay keyed on the type; only the name is the instrument's.
    const meta = DOC_TYPES[docType];
    const instrumentName = title || meta.label;
    const hasDriverParty = meta.parties.includes('DRIVER') || meta.parties.includes('BORROWER');
    const hasWitnessParty = meta.parties.includes('WITNESS');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Send the {instrumentName} for signature?</DialogTitle>
                    <DialogDescription>
                        {referenceNo
                            ? `${referenceNo} — generated from the current contract values.`
                            : 'The document is generated from the current contract values when you confirm.'}
                    </DialogDescription>
                </DialogHeader>

                <ul className="space-y-3 text-sm">
                    {hasDriverParty && (
                        <li className="flex gap-3">
                            <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <span>
                                <b>{driverName || 'The driver'}</b> sees it in the Primelane driver app and
                                signs there.
                            </span>
                        </li>
                    )}
                    {hasWitnessParty && (
                        <li className="flex gap-3">
                            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <span>
                                <b>{witnessName || 'The witness'}</b> signs through a link you copy from this
                                board and share with them. Nothing is emailed automatically, and the link only
                                becomes available once the driver has signed the hire purchase agreement —
                                which is the signature this document attests to.
                            </span>
                        </li>
                    )}
                    <li className="flex gap-3">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>
                            The wording freezes at the first signature. Changing a figure after that needs a
                            void and reissue.
                        </span>
                    </li>
                </ul>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={onConfirm} disabled={isPending}>
                        {isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="mr-2 h-4 w-4" />
                        )}
                        Send for signature
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
